import { ComponentProps, Ref } from 'preact';
import { TextTagList } from './TextTagList';
import { Flex } from '../UNSAFE_Flex';
import { TextFieldInput } from '../UNSAFE_TextField';
import { LiveRegion } from '../UNSAFE_LiveRegion';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';

type TextFieldInputProps = ComponentProps<typeof TextFieldInput>;
type TextTagListProps = ComponentProps<typeof TextTagList>;

type PassThroughTextFieldInputProps = Pick<
  TextFieldInputProps,
  | 'aria-controls'
  | 'aria-describedby'
  | 'aria-expanded'
  | 'aria-invalid'
  | 'aria-label'
  | 'autoFocus'
  | 'hasEmptyLabel'
  | 'hasInsideLabel'
  | 'id'
  | 'onBlur'
  | 'onFocus'
  | 'onInput'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'isRequired'
  | 'placeholder'
  | 'textAlign'
  | 'variant'
> & {
  inputRef?: Ref<HTMLInputElement>;
};
type PassThroughTextTagListProps = Pick<
  TextTagListProps,
  'onExitNavigation' | 'onRemove' | 'removeIcon'
>;

type Props = PassThroughTextFieldInputProps &
  PassThroughTextTagListProps & {
    displayValue?: string;
    isTextTagListShown?: boolean;
    isUserFiltering?: boolean;
    liveRegionText?: string;
    selectedValuesDescriptionId?: string;
    textTagListData: TextTagListProps['data'];
    textTagListRef?: TextTagListProps['ref'];
    userInput?: string;
    virtualKeyboard?: 'auto' | 'email' | 'number' | 'search' | 'tel' | 'text' | 'url';
  };

export function SelectMultipleFieldInput({
  displayValue,
  isTextTagListShown,
  isUserFiltering,
  liveRegionText,
  onExitNavigation,
  onRemove,
  removeIcon,
  selectedValuesDescriptionId,
  textTagListData,
  textTagListRef,
  userInput,
  virtualKeyboard,
  ...passThroughTextFieldInputProps
}: Props) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  return (
    <>
      {isTextTagListShown && (
        <TextTagList
          aria-label={translations.selectMultiple_selectedValues()}
          data={textTagListData}
          hasInsideLabel={passThroughTextFieldInputProps.hasInsideLabel}
          maxWidth="50%"
          onExitNavigation={onExitNavigation}
          onRemove={onRemove}
          ref={textTagListRef}
          removeIcon={removeIcon}
        />
      )}
      <Flex flex={1}>
        <TextFieldInput
          aria-autocomplete="list"
          autoComplete="off"
          // When focused, we show the tag list, which can be treated as having prefix
          hasPrefix={isTextTagListShown}
          role="combobox"
          spellcheck={false}
          type={virtualKeyboard === 'auto' ? 'search' : virtualKeyboard}
          value={isUserFiltering || isTextTagListShown ? userInput : displayValue}
          {...passThroughTextFieldInputProps}
        />
      </Flex>
      {
        // Show description text when focused as we will not be
        // showing display text in the input.
        isTextTagListShown && (
          <HiddenAccessible
            id={selectedValuesDescriptionId}
            // We want the content to be hidden for screen readers too by default.
            // But, it will be overridden when we link the aria-describedby attribute.
            // Otherwise, the content here will be read twice.
            isHidden={true}>
            {displayValue}
          </HiddenAccessible>
        )
      }
      <LiveRegion>{liveRegionText}</LiveRegion>
    </>
  );
}
